import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'
import remarkDirective from 'remark-directive'
import remarkGfm from 'remark-gfm'

import { isPlainClick } from '../shell/links.ts'
import { remarkAdmonitions } from './admonitions.ts'
import { CodeBlock } from './CodeBlock.tsx'
import { DOC_DIRECTIVES, remarkDocDirectives } from './directives.tsx'
import { Mermaid } from './Mermaid.tsx'
import { isInternalHref, rewriteHref, rewriteImageSrc } from './rewrite.ts'

// The docs renderer.
//
// `rehype-raw` is on because the content contains real HTML — 30 `<a target=
// "_blank">` links and 15 `<br/>`s that Docusaurus rendered natively. It is safe
// here in a way it would not be for user input: every byte this component sees
// comes from `content/`, which is first-party markdown committed to this repo and
// inlined at build time. Nothing at runtime can reach it.
//
// `rehype-slug` is what makes `#deliverymethod` work. It uses github-slugger,
// the same slugger Docusaurus used, so the ~130 anchor links in the content keep
// resolving to the same ids they resolved to on docs.curri.com. That parity is
// asserted in tests/docs-links.unit.test.ts rather than assumed.

const REMARK_PLUGINS = [
  remarkGfm,
  remarkDirective,
  remarkAdmonitions,
  remarkDocDirectives,
]
const REHYPE_PLUGINS = [rehypeRaw, rehypeSlug]

const LANGUAGE = /language-([\w-]+)/

// react-markdown hands every component the mdast `node` it came from. It must be
// dropped before the rest of the props reach a DOM element, or React warns about
// an unrecognised attribute on every heading, link, and image on the page.
type WithNode<T> = T & { node?: unknown }

const headingWithAnchor =
  (Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') =>
  ({ id, children, node: _node, ...props }: WithNode<ComponentPropsWithoutRef<'h2'>>) => (
    <Tag id={id} {...props}>
      {children}
      {id && (
        // The affordance for "give me a link to this section" — which is how
        // most of the cross-links in these docs were written in the first place.
        //
        // Hidden from the accessibility tree, GitHub-style. It lives *inside* the
        // heading, so anything it contributes lands in the heading's accessible
        // name: while it was exposed, every heading on every page was announced
        // as "Introduction#". Screen-reader and keyboard users reach these
        // sections through the contents panel, which is a real list of links.
        <a
          aria-hidden="true"
          className="curri-heading-anchor"
          href={`#${id}`}
          tabIndex={-1}
        >
          #
        </a>
      )}
    </Tag>
  )

export const Markdown = ({
  markdown,
  pagePath,
  onNavigate,
}: {
  markdown: string
  /** The page's portal path; relative links resolve against it. */
  pagePath: string
  onNavigate: (path: string) => void
}) => (
  <ReactMarkdown
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={REHYPE_PLUGINS}
    components={{
      // CodeBlock renders its own <pre>, so the wrapper is dropped rather than
      // nested — a <pre> inside a <pre> breaks whitespace handling.
      pre: ({ children }: { children?: ReactNode }) => <>{children}</>,
      code: ({ className, children, node: _node, ...props }) => {
        const language = LANGUAGE.exec(className ?? '')?.[1]
        const text = String(children).replace(/\n$/, '')

        if (language === 'mermaid') return <Mermaid chart={text} />
        if (language) return <CodeBlock code={text} language={language} />
        // A fence with no language still spans lines; inline code never does.
        if (text.includes('\n')) return <CodeBlock code={text} language="text" />

        return (
          <code className="curri-inline-code" {...props}>
            {children}
          </code>
        )
      },
      a: ({ href, children, node: _node, ...props }) => {
        const target = rewriteHref(href ?? '', pagePath)

        if (!isInternalHref(target)) {
          // External links keep the author's own `target`/`rel` intent, but
          // never without `noopener` — several were authored as bare
          // `target="_blank"`.
          const external = props.target === '_blank'
          return (
            <a
              href={target}
              {...props}
              rel={external ? 'noopener noreferrer' : props.rel}
            >
              {children}
            </a>
          )
        }

        return (
          <a
            href={target}
            {...props}
            onClick={event => {
              // Anchor-only links are the browser's job — intercepting them
              // would break in-page navigation to a heading.
              if (target.startsWith('#') || !isPlainClick(event)) return
              event.preventDefault()
              onNavigate(target)
            }}
          >
            {children}
          </a>
        )
      },
      img: ({ src, alt, node: _node, ...props }) => (
        <img
          src={rewriteImageSrc(typeof src === 'string' ? src : '')}
          alt={alt ?? ''}
          loading="lazy"
          {...props}
        />
      ),
      h1: headingWithAnchor('h1'),
      h2: headingWithAnchor('h2'),
      h3: headingWithAnchor('h3'),
      h4: headingWithAnchor('h4'),
      h5: headingWithAnchor('h5'),
      h6: headingWithAnchor('h6'),
      // Wide GraphQL argument tables are the norm in these docs; the wrapper is
      // what keeps them scrolling inside the article instead of widening the page.
      table: ({ children, node: _node, ...props }) => (
        <div className="curri-table-scroll">
          <table {...props}>{children}</table>
        </div>
      ),
      // Registered `::directive`s arrive as <section data-directive="…"> (see
      // directives.tsx) and mount the matching React component; any other
      // section — the content contains raw HTML — renders untouched.
      section: ({ children, node: _node, ...props }) => {
        const rest = props as ComponentPropsWithoutRef<'section'> &
          Record<string, unknown>
        const name = rest['data-directive']
        if (typeof name === 'string' && name in DOC_DIRECTIVES) {
          const Directive = DOC_DIRECTIVES[name]
          const attrs = JSON.parse(
            String(rest['data-directive-attrs'] ?? '{}')
          ) as Record<string, string>
          return (
            <Directive attrs={attrs} onNavigate={onNavigate}>
              {children}
            </Directive>
          )
        }
        return <section {...props}>{children}</section>
      },
    }}
  >
    {markdown}
  </ReactMarkdown>
)
